/** Yes/no and choice editors; each commits on change. */
import { Checkbox } from '@codex/ui';
import { useId } from 'react';
import type { EditorProps } from './shared.js';

const optionsOf = (field: EditorProps['field']): readonly string[] =>
  'options' in field.config ? field.config.options : [];

export function BooleanEditor({ field, value, error, commit }: EditorProps) {
  return (
    <div className="cx-pills__field">
      <Checkbox
        label={field.label}
        checked={value === true}
        onChange={(checked) => commit(checked)}
      />
      {error !== null && <span className="cx-pills__error">{error}</span>}
    </div>
  );
}

export function SelectEditor({ field, value, error, commit }: EditorProps) {
  const id = useId();
  const current = typeof value === 'string' ? value : '';
  const options = optionsOf(field);
  // A stored value no option names any more stays visible instead of vanishing.
  const shown = current !== '' && !options.includes(current) ? [...options, current] : options;
  return (
    <div className="cx-field">
      <label htmlFor={id} className="cx-field__label">
        {field.label}
      </label>
      <span className="cx-input" data-control-boundary="">
        <select
          id={id}
          className="cx-input__el"
          value={current}
          aria-invalid={error !== null ? true : undefined}
          aria-describedby={error !== null ? `${id}-error` : undefined}
          onChange={(event) => commit(event.currentTarget.value)}
        >
          <option value="">None</option>
          {shown.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </span>
      {error !== null && (
        <span id={`${id}-error`} className="cx-field__description">
          {error}
        </span>
      )}
    </div>
  );
}

export function MultiSelectEditor({ field, value, error, commit }: EditorProps) {
  const chosen = Array.isArray(value) ? value : [];
  const options = optionsOf(field);
  const shown = [...options, ...chosen.filter((c) => !options.includes(c))];
  const toggle = (option: string, on: boolean) => {
    const next = on ? [...chosen, option] : chosen.filter((c) => c !== option);
    commit(shown.filter((o) => next.includes(o)));
  };
  return (
    <fieldset className="cx-pills__options">
      <legend>{field.label}</legend>
      {shown.map((option) => (
        <Checkbox
          key={option}
          label={option}
          checked={chosen.includes(option)}
          onChange={(on) => toggle(option, on)}
        />
      ))}
      {error !== null && <span className="cx-pills__error">{error}</span>}
    </fieldset>
  );
}
